import type { TFunction } from 'i18next';

import type { FilterDef, FilterOption } from '@nucleus-commerce/admin-kit/index-table';
import {
  CollectionKind,
  CollectionRole,
  CollectionStatus,
  type Collection,
} from '@nucleus-commerce/api-client/admin';

/** Fields GET /stores/{storeId}/collections sorts on. The product count is computed per page, so not a sort. */
export const COLLECTION_SORTS = ['title', 'updatedAt'] as const;

const valuesOf = (values: Record<string, string>): FilterOption[] =>
  Object.values(values).map((value) => ({ value, label: value }));

/**
 * The filters as the URL and the API spell them, for the route's search schema and loader. The
 * enum `options` drop unknown values the same way the page's filters do, so the prefetch and the
 * page share a query key.
 */
export const COLLECTION_FILTER_FIELDS = [
  { key: 'kind', type: 'enum', options: valuesOf(CollectionKind) },
  { key: 'status', type: 'enum', options: valuesOf(CollectionStatus) },
  { key: 'role', type: 'enum', options: valuesOf(CollectionRole) },
  { key: 'updatedAt', type: 'date-range' },
] as const;

/** Filters of the collections table, in the order the filter menu lists them. `t` is the `collections` namespace. */
export function collectionFilters(t: TFunction): Array<FilterDef<Collection>> {
  const options = (values: Record<string, string>, prefix: string): FilterOption[] =>
    valuesOf(values).map(({ value }) => ({ value, label: t(`${prefix}.${value}`) }));

  return [
    {
      key: 'kind',
      label: t('filters.kind'),
      type: 'enum',
      pinned: true,
      options: options(CollectionKind, 'kind'),
    },
    {
      key: 'status',
      label: t('filters.status'),
      type: 'enum',
      pinned: true,
      options: options(CollectionStatus, 'status'),
    },
    {
      key: 'role',
      label: t('filters.role'),
      type: 'enum',
      options: options(CollectionRole, 'role'),
    },
    { key: 'updatedAt', label: t('filters.updatedAt'), type: 'date-range', aliases: ['updated'] },
  ];
}
